import { normalizeUtcIsoTimestamp } from './utc-timestamp'

/**
 * Normalize values declared as JSON Schema `format: date-time`.
 *
 * Missing and nullable optional fields are preserved. Untyped values and other
 * temporal formats such as `date` and `time` are intentionally untouched.
 */
export function normalizeJsonSchemaDateTimes(
  value: unknown,
  schema: unknown,
  path: string
): unknown {
  if (!isPlainObject(schema)) return value
  if (schema.format === 'date-time') {
    if (value === undefined || value === null || value === '') return value
    return normalizeUtcIsoTimestamp(value, path, { epochUnit: 'milliseconds' })
  }

  let normalized = value
  for (const combinator of ['allOf', 'anyOf', 'oneOf'] as const) {
    if (!Array.isArray(schema[combinator])) continue
    for (const branch of schema[combinator]) {
      if (schemaContainsDateTime(branch)) {
        normalized = normalizeJsonSchemaDateTimes(normalized, branch, path)
      }
    }
  }

  if (Array.isArray(normalized) && isPlainObject(schema.items)) {
    return normalized.map((item, index) =>
      normalizeJsonSchemaDateTimes(item, schema.items, `${path}[${index}]`)
    )
  }
  if (!isPlainObject(normalized)) return normalized

  const properties = isPlainObject(schema.properties) ? schema.properties : {}
  const out: Record<string, unknown> = { ...normalized }
  for (const [key, propertySchema] of Object.entries(properties)) {
    if (Object.hasOwn(normalized, key)) {
      out[key] = normalizeJsonSchemaDateTimes(normalized[key], propertySchema, `${path}.${key}`)
    }
  }
  return out
}

function schemaContainsDateTime(schema: unknown): boolean {
  if (!isPlainObject(schema)) return false
  if (schema.format === 'date-time') return true
  if (isPlainObject(schema.items) && schemaContainsDateTime(schema.items)) return true
  if (
    isPlainObject(schema.properties) &&
    Object.values(schema.properties).some(schemaContainsDateTime)
  ) {
    return true
  }
  return ['allOf', 'anyOf', 'oneOf'].some(
    (key) => Array.isArray(schema[key]) && schema[key].some(schemaContainsDateTime)
  )
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
